import { useToggle } from 'react-use';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { DownOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { Checkbox, Col, Row, Select } from 'antd';
import { logCommonView } from '@/lib/service';
import { EventSource } from '@/shared/types/event-types';
import { PRICE_OPTIONS } from '@/shared/constants/app';
import './index.less';
const FilterSidebar = ({
  params,
  designers,
  categoryOptions,
  onChangeSearch,
}: {
  designers: { [key: string]: any };
  categoryOptions: { [key: string]: any }[];
  params: { [key: string]: any };
  onChangeSearch: (type: string, value: Array<string>) => void;
}) => {
  const [categoriesExpanded, categoriesExpandedFun] = useToggle(true);
  const [designersExpanded, designersExpandedFun] = useToggle(true);
  const [priceExpanded, priceExpandedFun] = useToggle(true);
  const [priceValue, setPriceValue] = useState<string[]>([]);
  const [designerValue, setDesignerValue] = useState<string[] | undefined>([]);
  const [categoryValue, setCategoryValue] = useState<string[]>([]);

  const getPriceDefaultValue = useCallback(() => {
    if (isEmpty(params.minPriceRanges)) return [];
    const ranges = JSON.parse(params.minPriceRanges);
    const ret: string[] = [];
    ranges.forEach(function (range: string) {
      ret.push('[' + range + ']');
    });
    return ret;
  }, [params.minPriceRanges]);

  const getDesignerDefaultValue = useCallback(() => {
    if (isEmpty(params.storeId)) return [];
    const arr = params.storeId.split(',');
    arr.forEach((item: string, i: number) => {
      arr[i] = +arr[i];
    });
    return arr;
  }, [params.storeId]);

  useEffect(() => {
    setPriceValue(getPriceDefaultValue());
    setDesignerValue(getDesignerDefaultValue());
    setCategoryValue(!isEmpty(params.category) ? params.category.split(',') : []);
  }, [getDesignerDefaultValue, getPriceDefaultValue, params]);

  const getDictValues = useCallback((dict: { [key: string]: any }) => {
    return Object.keys(dict).map(function (key) {
      return dict[key];
    });
  }, []);

  const designerOptions = useMemo(() => {
    return getDictValues(designers).sort((a, b) => {
      return a.label?.localeCompare(b.label);
    });
  }, [designers, getDictValues]);

  const handleDesignerChange = useCallback(
    (values: string[]) => {
      onChangeSearch?.('designer', values);
    },
    [onChangeSearch],
  );
  return (
    <div className="filter-sidebar">
      <span className="filter-by-text utility-big">Filters</span>
      <span
        className="clear-filters utility-small"
        onClick={() => {
          setPriceValue([]);
          setDesignerValue([]);
          setCategoryValue([]);
          onChangeSearch?.('clearFilters', []);
        }}
      >
        Clear all filters
      </span>
      <div className="section-wrapper">
        <div className="header" onClick={categoriesExpandedFun}>
          <div>
            <span className="section-title utility-big">Category</span>
            {categoryValue.length > 0 && (
              <span className="count-filters utility-big">({categoryValue.length})</span>
            )}
          </div>
          <DownOutlined
            className={classNames('expand', {
              expanded: categoriesExpanded,
            })}
          />
        </div>
        <div
          className={classNames('content', {
            expanded: categoriesExpanded,
          })}
        >
          <Checkbox.Group
            value={categoryValue}
            onChange={(values) => {
              onChangeSearch?.('category', (values || []) as string[]);
            }}
          >
            <Row>
              {categoryOptions.map((item, index) => (
                <Col span={24} key={`checkbox-item-${index}`} className="checkbox">
                  <Checkbox
                    value={item.category}
                    onChange={(e) => {
                      logCommonView(EventSource.SHOP_ALL_FILTERS, `Category_${item.category}`);
                      if (e.target.checked) {
                        setCategoryValue((v) => [...v, item.category]);
                      } else {
                        setCategoryValue((v) => {
                          return v.filter((category) => category !== item.category);
                        });
                      }
                    }}
                  >
                    {item.category}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </div>
      </div>
      <div className="section-wrapper">
        <div className="header" onClick={designersExpandedFun}>
          <div>
            <span className="section-title utility-big">Creators</span>
            {designerValue.length > 0 && (
              <span className="count-filters utility-big">({designerValue.length})</span>
            )}
          </div>
          <DownOutlined
            className={classNames('expand', {
              expanded: designersExpanded,
            })}
          />
        </div>
        <div className="designer-filter" id="designer-filter">
          <Select
            mode="multiple"
            placeholder="Choose A Creator"
            value={designerValue}
            // getPopupContainer={() => document.getElementById('designer-filter')}
            onChange={handleDesignerChange}
            style={{ width: '100%' }}
            options={designerOptions}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      </div>
      <div className="section-wrapper">
        <div className="header" onClick={priceExpandedFun}>
          <div>
            <span className="section-title utility-big">Price</span>
            {priceValue.length > 0 && (
              <span className="count-filters utility-big">({priceValue.length})</span>
            )}
          </div>
          <DownOutlined
            className={classNames('expand', {
              expanded: priceExpanded,
            })}
          />
        </div>
        <div
          className={classNames('content', {
            expanded: priceExpanded,
          })}
        >
          <Checkbox.Group
            value={priceValue}
            onChange={(values) => {
              onChangeSearch?.('price', (values || []) as string[]);
            }}
          >
            <Row>
              {getDictValues(PRICE_OPTIONS).map((item, index) => (
                <Col span={24} key={`checkbox-item-${index}`} className="checkbox">
                  <Checkbox
                    value={item.value}
                    onChange={(e) => {
                      if (e.target.checked) {
                        logCommonView(EventSource.SHOP_ALL_FILTERS, `Price_${item.value}`);
                        setPriceValue((v) => [...v, item.value]);
                      } else {
                        setPriceValue((v) => {
                          return v.filter((category) => category !== item.value);
                        });
                      }
                    }}
                  >
                    {item.label}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;
